import { useState, useCallback } from 'react';

/**
 * Custom hook for form handling with validation
 * 
 * @param {Object} initialValues - Initial form values
 * @param {Function} validate - Validation function
 * @param {Function} onSubmit - Submit handler function
 * @returns {Object} - Form state and handlers
 */
export default function useForm(initialValues, validate, onSubmit) {
  // State for form values, errors, and submission status
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  // Handle input changes
  const handleChange = useCallback((event) => {
    const { name, value, type, checked } = event.target;
    
    // Handle different input types
    const inputValue = type === 'checkbox' ? checked : value;
    
    setValues(prevValues => ({
      ...prevValues,
      [name]: inputValue
    }));
    
    // Mark field as touched
    if (!touched[name]) {
      setTouched(prevTouched => ({
        ...prevTouched,
        [name]: true
      }));
    }
    
    // Clear error when value changes
    if (errors[name]) {
      setErrors(prevErrors => ({
        ...prevErrors,
        [name]: null
      }));
    }
  }, [touched, errors]);
  
  // Handle input blur (for validation on blur)
  const handleBlur = useCallback((event) => {
    const { name } = event.target;
    
    // Mark field as touched
    setTouched(prevTouched => ({
      ...prevTouched,
      [name]: true
    }));
    
    // Validate only this field
    if (validate) {
      const validationErrors = validate(values);
      
      if (validationErrors[name]) {
        setErrors(prevErrors => ({
          ...prevErrors,
          [name]: validationErrors[name]
        }));
      }
    }
  }, [values, validate]);
  
  // Validate all fields
  const validateForm = useCallback(() => {
    if (!validate) return {};
    
    const validationErrors = validate(values);
    setErrors(validationErrors);
    
    // Mark all fields as touched
    const allTouched = {};
    Object.keys(values).forEach(key => {
      allTouched[key] = true;
    });
    setTouched(allTouched);
    
    return validationErrors;
  }, [values, validate]);
  
  // Handle form submission
  const handleSubmit = useCallback(async (event) => {
    if (event) event.preventDefault();
    
    // Set submission status
    setIsSubmitting(true);
    setIsSubmitted(true);
    
    // Validate form
    const validationErrors = validateForm();
    
    // Check if there are any errors
    const hasErrors = Object.keys(validationErrors).length > 0;
    
    if (!hasErrors && onSubmit) {
      try {
        await onSubmit(values);
        // Clear errors on successful submission
        setErrors({});
      } catch (error) {
        // Handle submission error
        console.error('Form submission error:', error);
        
        // Set form-level error if it's a string
        if (typeof error === 'string') {
          setErrors(prevErrors => ({
            ...prevErrors,
            _form: error
          }));
        } else if (error && typeof error === 'object') {
          // Handle error object (e.g., from API)
          setErrors(prevErrors => ({
            ...prevErrors,
            ...error
          }));
        }
      }
    }
    
    setIsSubmitting(false);
  }, [values, validateForm, onSubmit]);
  
  // Reset form to initial values
  const resetForm = useCallback(() => {
    setValues(initialValues);
    setErrors({});
    setTouched({});
    setIsSubmitted(false);
    setIsSubmitting(false);
  }, [initialValues]);
  
  // Set a specific field value
  const setFieldValue = useCallback((name, value) => {
    setValues(prevValues => ({
      ...prevValues,
      [name]: value
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prevErrors => ({
        ...prevErrors,
        [name]: null
      }));
    }
  }, [errors]);
  
  // Set multiple field values at once
  const setMultipleValues = useCallback((newValues) => {
    setValues(prevValues => ({
      ...prevValues,
      ...newValues
    }));
    
    // Clear errors for updated fields
    const clearedErrors = { ...errors };
    Object.keys(newValues).forEach(key => {
      if (clearedErrors[key]) {
        delete clearedErrors[key];
      }
    });
    
    setErrors(clearedErrors);
  }, [errors]);
  
  // Set a specific error message
  const setFieldError = useCallback((name, error) => {
    setErrors(prevErrors => ({
      ...prevErrors,
      [name]: error
    }));
  }, []);
  
  return {
    // State
    values,
    errors,
    touched,
    isSubmitting,
    isSubmitted,
    
    // Event handlers
    handleChange,
    handleBlur,
    handleSubmit,
    
    // Utility functions
    resetForm,
    setFieldValue,
    setMultipleValues,
    setFieldError,
    validateForm
  };
}